import type React from 'react';
import { BORDERS, SPACING } from '../../../../constants/design';
import { getBorderColors, getSurfaceColors, getTextColors } from '../../../../utils/colorUtils';

interface ThemeModePreviewProps {
  mode: 'light' | 'dark' | 'system';
  size?: number;
}

export const ThemeModePreview: React.FC<ThemeModePreviewProps> = ({ mode, size = 48 }) => {
  // Check if the preview should show dark mode (for system mode, check system preference)
  const isDark =
    mode === 'dark' ||
    (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Use theme-specific tokens for accurate preview representation
  const surfaceColors = getSurfaceColors(isDark ? 'dark' : 'light');
  const textColors = getTextColors(isDark ? 'dark' : 'light');
  const borderColors = getBorderColors(isDark ? 'dark' : 'light');

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        padding: SPACING.xs,
        border: `${BORDERS.width.thin} solid ${borderColors.primary}`,
        borderRadius: BORDERS.radius.sm,
        background: surfaceColors.container,
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      {/* Header bar - represents app header */}
      <div
        style={{
          width: '100%',
          height: '28%',
          background: surfaceColors.elevated,
          borderRadius: `${BORDERS.radius.sm} ${BORDERS.radius.sm} 0 0`,
          border: `${BORDERS.width.thin} solid ${borderColors.primary}`,
          minHeight: '8px',
          position: 'relative',
        }}
      >
        {/* Small header indicator dot */}
        <div
          style={{
            position: 'absolute',
            left: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '3px',
            height: '3px',
            borderRadius: '50%',
            background: textColors.secondary,
          }}
        />
      </div>

      {/* Content bar 1 - wider, represents main content */}
      <div
        style={{
          width: '85%',
          height: '22%',
          background: textColors.tertiary,
          borderRadius: BORDERS.radius.sm,
          minHeight: '6px',
          opacity: 0.6,
        }}
      />

      {/* Content bar 2 - medium width */}
      <div
        style={{
          width: '70%',
          height: '18%',
          background: textColors.tertiary,
          borderRadius: BORDERS.radius.sm,
          minHeight: '5px',
          opacity: 0.5,
        }}
      />

      {/* Content bar 3 - narrow, represents smaller content */}
      <div
        style={{
          width: '55%',
          height: '14%',
          background: textColors.tertiary,
          borderRadius: BORDERS.radius.sm,
          minHeight: '4px',
          opacity: 0.4,
        }}
      />
    </div>
  );
};
